/**
 * reports-local.ts — local report CRUD against the `reports` IndexedDB store.
 *
 * This is the source of truth for the technician's own reports while offline.
 * updatedAt is stamped at CALL TIME (ADR-7: not flush time) to preserve
 * last-write-wins semantics on the server.
 */

import { getDb } from "../stores/db.js";
import type { LocalReport } from "../types.js";

const STORE = "reports";

/** Read one local report by its localId. */
export async function getReport(localId: string): Promise<LocalReport | undefined> {
  const db = await getDb();
  return db.get(STORE, localId) as Promise<LocalReport | undefined>;
}

/** Read all local reports (the technician's full list). */
export async function getAllReports(): Promise<LocalReport[]> {
  const db = await getDb();
  return db.getAll(STORE) as Promise<LocalReport[]>;
}

/**
 * Write (upsert) a local report.
 * Stamps updatedAt at the moment of this call (ADR-7) unless an explicit
 * updatedAt override is provided — callers that need a shared timestamp
 * (e.g. enqueueReport) can pass it to keep the reports store and the outbox
 * SyncInformeItem in sync (both get the exact same last-write-wins anchor).
 */
export async function putReport(record: LocalReport, updatedAt?: string): Promise<void> {
  const db = await getDb();
  const ts = updatedAt ?? new Date().toISOString();
  // Preserve createdAt across edits: explicit value wins (server creation time
  // when mirroring), else keep the existing one, else stamp it now on first insert.
  let createdAt = record.createdAt;
  if (!createdAt) {
    const existing = (await db.get(STORE, record.localId)) as LocalReport | undefined;
    createdAt = existing?.createdAt ?? ts;
  }
  await db.put(STORE, {
    ...record,
    createdAt,
    updatedAt: ts,
  });
}

/** Delete one local report by its localId (no-op if it does not exist). */
export async function deleteReport(localId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, localId);
}

/** Look up a report by its server-assigned id (uses by-serverId index). */
export async function getReportByServerId(serverId: string): Promise<LocalReport | undefined> {
  const db = await getDb();
  const idx = db.transaction(STORE).store.index("by-serverId");
  return idx.get(serverId) as Promise<LocalReport | undefined>;
}

/** Get all children of a local parent report (uses by-parentLocalId index). */
export async function getChildReports(parentLocalId: string): Promise<LocalReport[]> {
  const db = await getDb();
  const idx = db.transaction(STORE).store.index("by-parentLocalId");
  return idx.getAll(parentLocalId) as Promise<LocalReport[]>;
}
